/**
 * apiClient - wrapper na backend calls.
 *
 * Generatory AI + saldo + galeria + projekty + marka + agencja + admin -> Railway
 * (api.adresflow.com) przez fetchRailway (JWT). Generatory są SYNCHRONICZNE —
 * resultImage wraca inline w 200 (bez pollingu).
 *
 * Cały panel chodzi po Railway. Ostatnie wywołanie Supabase Edge w tym pliku
 * zniknęło 2026-08-18 (płatności, unlock-photos, status joba) — patrz
 * ai/decisions/2026-08-18-wygaszenie-v1-audyt-edge-functions.md.
 */
import type {
  ApiResponse,
  StartProcessingRequest,
  GeneratorResponse,
  FloorPlanStepResponse,
  JobStatusResponse,
  OfferFields,
  UpdateOfferInput,
  KwData,
  ValuationSnapshot,
  KwLookup,
  LightingProfile,
  RetouchOption,
} from "@adresflow-v2/shared";
import { supabase } from "@/integrations/supabase/client";
import { ERROR_CODES, ERROR_MESSAGES_PL } from "@adresflow-v2/shared";

/**
 * Bazowy URL Railway API. Odporny na zepsute env:
 * gdy VITE_API_BASE_URL jest puste lub bez hosta (np. niezresolvowana referencja
 * Railway `https://${{api.RAILWAY_PUBLIC_DOMAIN}}` → samo "https://"), wpadlibyśmy
 * w requesty typu `https://v1/...`. W takim wypadku fallback wyprowadzony z hosta
 * przeglądarki: `app.<domena>` → `api.<domena>` (niezależnie od TLD .pl/.com).
 */
function fallbackApiBase(): string {
  if (typeof window !== "undefined" && /^app\./i.test(window.location.host)) {
    return `${window.location.protocol}//${window.location.host.replace(/^app\./i, "api.")}`;
  }
  return "https://api.adresflow.com";
}

function resolveApiBase(): string {
  const raw = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
  // wymaga schematu + niepustego hosta (znak inny niż "/" zaraz po "//")
  if (raw && /^https?:\/\/[^/]+/i.test(raw)) {
    return raw.replace(/\/+$/, "");
  }
  const fb = fallbackApiBase();
  if (raw) console.warn(`[apiClient] VITE_API_BASE_URL nieprawidłowe ("${raw}") — fallback na ${fb}`);
  return fb;
}

const API_BASE_URL = resolveApiBase();

async function getJwt(): Promise<string | null> {
  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") &&
    localStorage.getItem("dev_admin_session") === "true"
  ) {
    return "dev-local-admin-token";
  }
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

// =====================================================
// Railway (api.adresflow.com) - AI generation
// =====================================================

let authRedirectStarted = false;
/**
 * Jednorazowe zejście do /auth po 401. Kilka zapytań potrafi dostać 401 w tej samej
 * sekundzie (hub odpala credits + entitlements + projects równolegle) — bez guardu
 * każde z nich wołałoby signOut i przestawiało location.
 *
 * `signOut({ scope: "local" })`: czyścimy TYLKO tę przeglądarkę. Globalny signOut
 * unieważniłby refresh tokeny wszędzie — czyli np. sesję tego samego usera w v1.
 */
/**
 * Strony, które muszą obsłużyć 401 NA MIEJSCU — twardy redirect gubi ich stan:
 * /payment-success traci session_id (event `purchase` przepada bezpowrotnie,
 * bo trackOnce pali klucz), /accept-invitation traci token zaproszenia i swój
 * własny ekran „Sesja wygasła" z linkiem zachowującym redirect.
 */
const AUTH_REDIRECT_EXEMPT = ["/payment-success", "/accept-invitation"];

async function redirectToAuthOnce(): Promise<void> {
  if (authRedirectStarted) return;
  if (AUTH_REDIRECT_EXEMPT.some((path) => window.location.pathname.startsWith(path))) return;
  authRedirectStarted = true;
  try {
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    // sesji mogło już nie być — cel jest jeden: wylądować na /auth
  }
  window.location.assign("/auth");
}

export async function fetchRailway<T>(path: string, init?: RequestInit): Promise<ApiResponse<T>> {
  if (!API_BASE_URL) {
    return { error: "API_BASE_URL not configured", code: "INTERNAL_ERROR" };
  }
  const jwt = await getJwt();
  if (!jwt) {
    return { error: "Brak zalogowanej sesji", code: "AUTH_MISSING" };
  }
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
        ...init?.headers,
      },
    });
    const body = await response.json().catch(() => ({}));
    if (response.status === 401) {
      // Sesja martwa (np. wylogowanie w v1 unieważnia refresh token globalnie).
      // Bez tej gałęzi panel renderował się normalnie z zerami — nieodróżnialnie
      // od „naprawdę masz zero kredytów" (issue #57). Czyścimy sesję i idziemy
      // do logowania; guard chroni przed lawiną przekierowań z równoległych zapytań.
      await redirectToAuthOnce();
      return { error: ERROR_MESSAGES_PL.AUTH_INVALID, code: ERROR_CODES.AUTH_INVALID };
    }
    if (!response.ok) {
      // Body błędu bywa puste (502/504 z HTML-em zamiast JSON-a). Bez tych domyślnych
      // wartości wracałby goły {}, który isApiError() uznaje za SUKCES — a caller
      // wywraca się na res.data.<pole>. Ten sam fallback co w fetchRailwayPublic.
      const err = body as Partial<{ error: string; code: string }>;
      return {
        error: err.error ?? `Błąd serwera (HTTP ${response.status})`,
        code: err.code ?? "INTERNAL_ERROR",
      };
    }
    return { data: body as T };
  } catch (err) {
    return {
      error: err instanceof Error ? err.message : "Network error",
      code: "INTERNAL_ERROR",
    };
  }
}

/**
 * Wariant fetchRailway BEZ wymogu sesji — dla publicznych endpointów, do których
 * user trafia z linku w mailu (może nie mieć aktywnego JWT). JWT dokładamy gdy jest,
 * ale jego brak nie blokuje requestu.
 */
export async function fetchRailwayPublic<T>(path: string, init?: RequestInit): Promise<ApiResponse<T>> {
  if (!API_BASE_URL) {
    return { error: "API_BASE_URL not configured", code: "INTERNAL_ERROR" };
  }
  const jwt = await getJwt().catch(() => null);
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        ...init?.headers,
      },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = body as Partial<{ error: string; code: string }>;
      return {
        error: err.error ?? "Wystąpił błąd. Spróbuj ponownie.",
        code: err.code ?? "INTERNAL_ERROR",
      };
    }
    return { data: body as T };
  } catch (err) {
    return {
      error: err instanceof Error ? err.message : "Network error",
      code: "INTERNAL_ERROR",
    };
  }
}

// ─── Auth: weryfikacja e-maila (Railway) ─────────────────────────────────────

export interface VerifyEmailResponse {
  success: boolean;
  alreadyVerified?: boolean;
}

/** POST /v1/auth/verify-email — publiczny (token z maila jest sekretem). */
export async function verifyEmailToken(token: string): Promise<ApiResponse<VerifyEmailResponse>> {
  return fetchRailwayPublic("/v1/auth/verify-email", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

// Ponowna wysyłka linku: sendVerificationEmail() — patrz sekcja „Weryfikacja e-maila" niżej.

/** Home Staging / Czysty pokój — sync, resultImage inline w odpowiedzi. */
export async function startProcessing(
  body: StartProcessingRequest,
): Promise<ApiResponse<GeneratorResponse>> {
  return fetchRailway("/v1/ai/start-processing", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ─── Sprzątanie i retusz (room-cleanup) ─────────────────────────────────────

export interface RoomCleanupArgs {
  /** Data URL — ten sam kontrakt co start-processing (a NIE surowe base64 remontu). */
  sourceImage: string;
  /** Z allowlisty API; brak = model oddaje swoją natywną proporcję. */
  aspectRatio?: string;
  lightingProfile?: LightingProfile;
  /** Przełączniki „co poprawiamy". Pusta lista jest odrzucana przez API (400). */
  options?: RetouchOption[];
  isRefresh?: boolean;
  previousJobId?: string;
  customInstructions?: string;
}

/** Retusz bez home stagingu — sync, resultImage inline w odpowiedzi. */
export async function roomCleanup(args: RoomCleanupArgs): Promise<ApiResponse<GeneratorResponse>> {
  return fetchRailway("/v1/ai/room-cleanup", { method: "POST", body: JSON.stringify(args) });
}

// ─── Wirtualny remont (room-arrangement) ────────────────────────────────────

export interface RoomArrangementArgs {
  imageBase64: string;   // surowe base64 (bez prefiksu data:)
  mimeType: string;      // image/jpeg | image/png | image/webp
  roomType: string;      // np. "Salon"
  style: string;         // czytelna nazwa stylu, np. "Skandynawski"
  userDescription?: string;
  aspectRatio: string;   // z allowlisty API
  isRefresh?: boolean;
  previousJobId?: string;
}

export async function roomArrangement(args: RoomArrangementArgs): Promise<ApiResponse<GeneratorResponse>> {
  return fetchRailway("/v1/ai/room-arrangement", { method: "POST", body: JSON.stringify(args) });
}

// ─── Zdjęcia biznesowe (team-photo) ─────────────────────────────────────────

export type TeamBackground =
  | { type: "dark" }
  | { type: "light" }
  | { type: "color"; color: string }
  | { type: "real"; place: string };

export interface TeamPhotoPerson {
  id: string;
  images: string[];            // 1–3 data URL
  clothingColor?: string;
  smileScale?: number;         // 0–10
  bodyType?: "slim" | "average" | "athletic" | "curvy" | "plus-size";
}

export interface TeamPhotoArgs {
  people: TeamPhotoPerson[];
  leaderId?: string | null;
  teamStyle?: string;
  photoStyle?: "candid" | "motion" | "posed" | "cinematic" | "editorial" | "documentary" | "creative";
  background?: TeamBackground;
  isRefresh?: boolean;
  previousJobId?: string;
}

export async function teamPhoto(args: TeamPhotoArgs): Promise<ApiResponse<GeneratorResponse>> {
  return fetchRailway("/v1/ai/team-photo", { method: "POST", body: JSON.stringify(args) });
}

// ─── Rzut 3D (floorplan-step) ───────────────────────────────────────────────

export interface FloorPlanStepArgs {
  step: 1 | 2 | 3;
  imageBase64?: string;   // data URL — wymagane tylko dla kroku 1
  previousJobId?: string; // wymagane dla kroków 2/3
  parentJobId?: string;
  styleDescription: string;
  isRefresh?: boolean;
  /** Geometria z floorplanAnalyze — włącza prompty sterowane rzutem. */
  verifiedJson?: string;
}

export async function floorplanStep(args: FloorPlanStepArgs): Promise<ApiResponse<FloorPlanStepResponse>> {
  return fetchRailway("/v1/ai/floorplan-step", { method: "POST", body: JSON.stringify(args) });
}

export interface FloorPlanAnalyzeResponse {
  /** ADR-JSON po audycie Agenta 2 i auto-leczeniu geometrii. */
  verifiedJson: string;
  /** Surowy wynik Agenta 1 — do diagnostyki. */
  rawJson: string;
}

/**
 * Analiza geometrii rzutu (Agent 1 + Agent 2). Darmowa, ~20–40 s.
 * Wynik przekazywany do każdego kroku generacji jako `verifiedJson`.
 */
export async function floorplanAnalyze(args: {
  imageBase64: string;
  skipVerification?: boolean;
}): Promise<ApiResponse<FloorPlanAnalyzeResponse>> {
  return fetchRailway("/v1/ai/floorplan-analyze", { method: "POST", body: JSON.stringify(args) });
}

// ─── Zabudowa działek (plot-step, action dispatch) ──────────────────────────
//
// Jeden endpoint POST /v1/plot/step z dispatchem po `action`:
//   prompt      — Gemini buduje prompt INPAINT z parametrów formularza (0 kr)
//   generate    — generacja obrazu, SYNCHRONICZNA: resultImage wraca inline (5 kr / 1 kr przy isEdit)
//   edit-prompt — modyfikacja promptu wg życzenia PL (0 kr, koszt schodzi przy generate)
//   save-blend  — podmiana result_image joba na zblendowany pełny kadr (0 kr)
//   save-marked — zapis zdjęcia z obrysem działki jako job galerii (0 kr)
// Canvas (crop / maska / blend) zostaje po stronie klienta — patrz utils/plotImage.ts.

export interface PlotPolygonPoint { xPct: number; yPct: number }

export interface PlotParams {
  style: string;
  plotArea: number;
  buildingCoverage: number;
  greenArea: number;
  floors: string;
  roofColor: string;
  facadeColor: string;
  garage: string;
  terrace: string;
  winterGarden: string;
  fence: string;
  pool: string;
}

export type PlotStepBody =
  | { action: "prompt"; imageBase64: string; params: PlotParams; polygonPoints: PlotPolygonPoint[]; imageAspectRatio: number }
  | { action: "generate"; prompt: string; imageBase64: string; maskBase64?: string; isEdit?: boolean; previousJobId?: string }
  | { action: "edit-prompt"; originalPrompt: string; editRequest: string }
  | { action: "save-blend"; jobId: string; imageBase64: string }
  | { action: "save-marked"; imageBase64: string };

export interface PlotStepResult {
  prompt?: string;
  jobId?: string;
  resultImage?: string;
  thumbnailImage?: string | null;
  purchased?: boolean;
  cost?: number;
}

export async function plotStep(body: PlotStepBody): Promise<ApiResponse<PlotStepResult>> {
  return fetchRailway("/v1/plot/step", { method: "POST", body: JSON.stringify(body) });
}

// =====================================================
// Supabase Edge - CRUD (legacy, zostawiamy w v1 backend)
// =====================================================

export interface GetCreditsResponse {
  balance: number;
  corrections: number;
  balanceId: string;
  isPaid: boolean;
  subscriptionTier?: string;
  agencyId?: string | null;
  agencyName?: string | null;
  agencyRole?: string | null;
}

/** Saldo kredytów — Railway GET /v1/credits (kształt 1:1 z legacy get-credits). */
export async function getCredits(): Promise<ApiResponse<GetCreditsResponse>> {
  return fetchRailway("/v1/credits");
}

export interface GalleryJob {
  id: string;
  variantType: string;
  status: "done" | "failed";
  resultImage: string | null;
  thumbnailImage?: string | null;
  purchased: boolean;
  createdAt: string;
  expiresAt?: string | null;
  projectId?: string | null;
}

export interface GetGalleryResponse {
  jobs: GalleryJob[];
}

export interface CreateCheckoutResponse {
  url: string;
}

/**
 * Tworzy Stripe Checkout session dla paczki kredytow.
 * Po sukcesie redirectujemy user'a na zwrocony url (Stripe hosted page).
 */
export interface VerifyPaymentResponse {
  success: boolean;
  credits?: number;
  packageName?: string;
  // Pola zwracane przez verify-payment (edge fn wspoldzielona z v1) — uzywane do
  // eventu analitycznego `purchase`. creditsAdded=0 + brak packageId gdy stripe-webhook
  // zdazyl przetworzyc sesje pierwszy (alreadyProcessed).
  creditsAdded?: number;
  packageId?: string | null;
  // Sesja subskrypcyjna (checkout_kind=subscription): plan i kwota zamiast paczki.
  kind?: "subscription";
  planId?: string | null;
  planName?: string | null;
  period?: string | null;
  amountTotalGrosze?: number | null;
}

/**
 * Po Stripe redirect na /payment-success?session_id=... weryfikujemy session
 * (idempotent z stripe-webhook - oba dodaja kredyty, transaction sentinel chroni przed duplikatami).
 * Frontend wola to zeby dostac confirmation + nazwy paczki do toastu.
 */
export async function verifyPayment(sessionId: string): Promise<ApiResponse<VerifyPaymentResponse>> {
  return fetchRailway<VerifyPaymentResponse>("/v1/payments/verify", {
    method: "POST",
    body: JSON.stringify({ session_id: sessionId }),
  });
}

/**
 * Zakup abonamentu ALBO zmiana planu na istniejącej subskrypcji.
 *
 * Dwie możliwe odpowiedzi: `url` (nowy zakup → checkout Stripe) albo `changed: true`
 * (klient miał już abonament → podmieniliśmy cenę na jego subskrypcji, bez wychodzenia
 * z aplikacji). Druga ścieżka nie ma URL-a, bo nie ma czego potwierdzać — różnica
 * dopisuje się do następnej faktury.
 *
 * `period` decyduje, którą cenę weźmie Stripe — roczna jest osobnym obiektem ceny,
 * nie mnożnikiem, więc przełącznik na cenniku musi tu dojść.
 * `seats` dotyczy tylko planów Biuro: to ŁĄCZNA liczba agentów (w cenie + dokupieni).
 */
export async function createSubscriptionCheckout(input: {
  planId: string;
  period: "monthly" | "yearly";
  seats?: number;
  /** Kod z losowania rabatu (usePromoOffer) — API nalicza go w koszyku Stripe. */
  promoCode?: string;
}): Promise<ApiResponse<{ url?: string; sessionId?: string; changed?: boolean; planId?: string }>> {
  return fetchRailway("/v1/payments/subscription-checkout", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Sesja Customer Portalu Stripe. Zwracany URL jest jednorazowy i wygasa —
 * przekierowujemy od razu, nie zapisujemy go w stanie.
 *
 * 403 MEMBER_CANNOT_MANAGE_BILLING → członek agencji (subskrypcją zarządza właściciel).
 * 404 NO_STRIPE_CUSTOMER → konto nigdy nie płaciło, więc nie ma czym zarządzać.
 */
export async function createCustomerPortalSession(): Promise<ApiResponse<{ url: string }>> {
  return fetchRailway("/v1/payments/customer-portal", { method: "POST" });
}

export async function createCheckout(
  packageId: string,
  /** Kod z losowania rabatu (usePromoOffer) — nalicza się, gdy plan nie daje własnego. */
  promoCode?: string,
): Promise<ApiResponse<CreateCheckoutResponse>> {
  const res = await fetchRailway<CreateCheckoutResponse>("/v1/payments/checkout", {
    method: "POST",
    body: JSON.stringify({ packageId, promoCode }),
  });
  // Zachowany kontrakt sprzed przepięcia: brak URL-a to błąd, nie „pusty sukces" —
  // inaczej przycisk płatności udawałby, że zadziałał.
  if ("data" in res && !res.data?.url) {
    return { error: "Brak URL płatności", code: "INTERNAL_ERROR" };
  }
  return res;
}

/** Paczka kredytów z publicznego cennika (GET /v1/payments/packages). */
export interface CreditPackageDto {
  id: string;
  /** Slug v2 (kawalerka/mieszkanie/dom); null gdy API nie zna nazwy z DB. */
  slug: string | null;
  name: string;
  credits: number;
  pricePln: number;
}

/**
 * GET /v1/payments/packages — aktywne paczki kredytów (Railway, publiczny).
 *
 * Slug rozwiązuje API, po tej samej mapie, po której rozwiązuje go checkout —
 * front nie trzyma własnej kopii nazw z DB (patrz ADR 2026-08-05).
 */
export async function getCreditPackages(): Promise<ApiResponse<{ packages: CreditPackageDto[] }>> {
  return fetchRailwayPublic("/v1/payments/packages");
}

export interface UnlockPhotosResponse {
  success: boolean;
  newBalance: number;
}

export async function unlockPhotos(jobIds: string[]): Promise<ApiResponse<UnlockPhotosResponse>> {
  // Brak kredytów API zwraca jako 200 z polem `error` w body (kształt odziedziczony
  // po v1 — front branchuje po treści, nie po statusie). fetchRailway przepuszcza
  // takie body do `data`, więc zachowanie jest identyczne jak przed przepięciem.
  return fetchRailway<UnlockPhotosResponse>("/v1/credits/unlock-photos", {
    method: "POST",
    body: JSON.stringify({ jobIds }),
  });
}

export async function getGallery(): Promise<ApiResponse<GetGalleryResponse>> {
  const res = await fetchRailway<{ items: GalleryJob[] }>("/v1/gallery");
  if ("error" in res) return res;
  return { data: { jobs: res.data!.items } };
}

// =====================================================
// Agency (Railway)
// =====================================================

export interface AgencyMember {
  userId: string;
  email: string;
  role: "owner" | "member";
  joinedAt: string;
  usage: { total: number; thisMonth: number };
  firstName: string | null;
  lastName: string | null;
}

export interface AgencyInvitation {
  id: string;
  email: string;
  token: string | null;
  status: "pending" | "accepted" | "revoked" | "expired";
  expiresAt: string;
  createdAt: string;
}

export interface GetAgencyResponse {
  hasAgency: false;
}

export interface GetAgencyWithDataResponse {
  hasAgency: true;
  agency: {
    id: string;
    name: string;
    logoUrl: string | null;
    createdAt: string;
    address: string | null;
    nip: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
  };
  balance: { total: number; corrections: number; isPaid: boolean };
  callerRole: "owner" | "member";
  members: AgencyMember[];
  invitations: AgencyInvitation[];
}

export async function getAgency(): Promise<ApiResponse<GetAgencyResponse | GetAgencyWithDataResponse>> {
  return fetchRailway("/v1/agency");
}

export async function createAgency(name: string): Promise<ApiResponse<{ agencyId: string }>> {
  return fetchRailway("/v1/agency", { method: "POST", body: JSON.stringify({ name }) });
}

export interface UpdateAgencyInput {
  name?: string;
  address?: string;
  nip?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export async function updateAgency(input: UpdateAgencyInput): Promise<ApiResponse<{ success: boolean }>> {
  return fetchRailway("/v1/agency", { method: "PUT", body: JSON.stringify(input) });
}

export interface InviteMemberResponse {
  inviteUrl: string;
  expiresAt: string;
  /** false = mail nie wyszedł (brak konfiguracji/błąd dostawcy) — pokaż link do skopiowania. */
  emailSent: boolean;
}

export async function inviteMember(email: string): Promise<ApiResponse<InviteMemberResponse>> {
  return fetchRailway("/v1/agency/invite", {
    method: "POST",
    body: JSON.stringify({ email: email.trim() }),
  });
}

export interface ResendInvitationResponse {
  emailSent: boolean;
  inviteUrl: string;
}

/** Ponowna wysyłka zaproszenia (mail + link zapasowy). Tylko właściciel biura. */
export async function resendAgencyInvitation(
  invitationId: string,
): Promise<ApiResponse<ResendInvitationResponse>> {
  return fetchRailway("/v1/agency/resend-invitation", {
    method: "POST",
    body: JSON.stringify({ invitationId }),
  });
}

/** Wycofanie oczekującego zaproszenia — zwalnia miejsce w biurze. Tylko właściciel. */
export async function revokeAgencyInvitation(
  invitationId: string,
): Promise<ApiResponse<{ success: boolean }>> {
  return fetchRailway(`/v1/agency/invitations/${encodeURIComponent(invitationId)}`, {
    method: "DELETE",
  });
}

export interface AcceptInvitationResponse {
  success?: boolean;
  agencyId?: string;
  mergedCredits?: number;
}

/**
 * Dołączenie do biura tokenem z zaproszenia.
 * Wcześniej strona wołała Edge Function `accept-invitation`, której nie ma już
 * w repo v1 — flow dołączania z nowego panelu nie miał szans zadziałać.
 * Źródłem prawdy jest POST /v1/agency/accept-invitation (RPC + merge kredytów).
 */
export async function acceptAgencyInvitation(
  token: string,
): Promise<ApiResponse<AcceptInvitationResponse> & { currentAgencyName?: string | null }> {
  return fetchRailway<AcceptInvitationResponse>("/v1/agency/accept-invitation", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export async function removeAgencyMember(memberId: string): Promise<ApiResponse<{ ok: boolean }>> {
  return fetchRailway(`/v1/agency/members/${encodeURIComponent(memberId)}`, { method: "DELETE" });
}

export async function updateAgencyMember(
  memberId: string,
  input: { firstName?: string; lastName?: string },
): Promise<ApiResponse<{ success: boolean }>> {
  return fetchRailway(`/v1/agency/members/${encodeURIComponent(memberId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function checkJobStatus(jobId: string): Promise<ApiResponse<JobStatusResponse>> {
  return fetchRailway<JobStatusResponse>(`/v1/gallery/items/${encodeURIComponent(jobId)}`);
}

// =====================================================
// Credits history (Railway)
// =====================================================

export interface CreditTransaction {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  amountPaidPln: number | null;
  createdAt: string;
}

export async function getCreditsHistory(): Promise<ApiResponse<{ transactions: CreditTransaction[] }>> {
  return fetchRailway("/v1/credits/history");
}

// =====================================================
// Projects (Railway) — requires migration 001_add_projects.sql
// =====================================================

// Projekt = oferta: pola bazowe + spłaszczone pola oferty (migracja 013). Pola oferty
// są opcjonalne (Partial) — starsze projekty / niepełne kreatory ich nie mają.
export interface Project extends Partial<OfferFields> {
  id: string;
  name: string;
  address: string | null;
  createdAt: string;
  jobCount: number;
}

/**
 * Lista ofert + limit planu. `offerLimit === null` znaczy „bez limitu”.
 * Pola są opcjonalne, bo starszy bundle może rozmawiać z nowym API i odwrotnie.
 * `tier` zniknęło razem z mapą OFFER_LIMITS — limit pochodzi z uprawnień planu.
 */
export interface GetProjectsResponse {
  projects: Project[];
  planId?: string | null;
  offerLimit?: number | null;
}

export async function getProjects(): Promise<ApiResponse<GetProjectsResponse>> {
  return fetchRailway("/v1/projects");
}

export async function getProject(projectId: string): Promise<ApiResponse<Project>> {
  return fetchRailway(`/v1/projects/${encodeURIComponent(projectId)}`);
}

export async function createProject(name: string, address?: string): Promise<ApiResponse<Project>> {
  return fetchRailway("/v1/projects", { method: "POST", body: JSON.stringify({ name, address }) });
}

export async function updateProject(projectId: string, patch: UpdateOfferInput): Promise<ApiResponse<Project>> {
  return fetchRailway(`/v1/projects/${encodeURIComponent(projectId)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

// ── Wyceny (Adresometr) ─────────────────────────────────────────────────────

/**
 * Zapisana wycena. `snapshot` przychodzi tylko z widoku szczegółu — lista celowo
 * go nie ciągnie, bo to kilkadziesiąt kilobajtów jsonb na rekord.
 */
export interface SavedValuation {
  id: string;
  address: string;
  city: string | null;
  propertyType: string;
  areaM2: number;
  estimatedValue: number;
  finalPpm: number;
  rangeLow: number | null;
  rangeHigh: number | null;
  createdAt: string;
  updatedAt: string;
  projectId: string | null;
  snapshot: ValuationSnapshot | null;
}

/**
 * Lista wycen. `limit` jest jawny, bo strona musi wiedzieć, o ile prosiła — komplet
 * równy limitowi znaczy „są starsze", a bez tej wiedzy lista ucinałaby się po cichu.
 */
export async function getValuations(limit: number): Promise<ApiResponse<{ valuations: SavedValuation[] }>> {
  return fetchRailway(`/v1/valuations?limit=${encodeURIComponent(String(limit))}`);
}

export async function getValuation(valuationId: string): Promise<ApiResponse<SavedValuation>> {
  return fetchRailway(`/v1/valuations/${encodeURIComponent(valuationId)}`);
}

export async function createValuation(snapshot: ValuationSnapshot): Promise<ApiResponse<SavedValuation>> {
  return fetchRailway("/v1/valuations", { method: "POST", body: JSON.stringify({ snapshot }) });
}

export async function updateValuation(
  valuationId: string,
  snapshot: ValuationSnapshot,
): Promise<ApiResponse<SavedValuation>> {
  return fetchRailway(`/v1/valuations/${encodeURIComponent(valuationId)}`, {
    method: "PUT",
    body: JSON.stringify({ snapshot }),
  });
}

export async function deleteValuation(valuationId: string): Promise<ApiResponse<{ ok: boolean }>> {
  return fetchRailway(`/v1/valuations/${encodeURIComponent(valuationId)}`, { method: "DELETE" });
}

// Opis oferty AI (Railway) — generuje tekst ogłoszenia z pól oferty + wyceny. Kosztuje kredyty.
export async function generateOfferDescription(projectId: string): Promise<ApiResponse<{ description: string }>> {
  return fetchRailway("/v1/ai/offer-description", {
    method: "POST",
    body: JSON.stringify({ projectId }),
  });
}

export async function deleteProject(projectId: string): Promise<ApiResponse<{ ok: boolean }>> {
  return fetchRailway(`/v1/projects/${encodeURIComponent(projectId)}`, { method: "DELETE" });
}

// Księga wieczysta (Railway) — pobranie danych KW z portalu EKW przez zewnętrznego
// dostawcę. Synchroniczne (~30–40s, front pokazuje loader). Zwraca znormalizowany KwData.
/**
 * Sprawdzenie księgi — ZAWSZE świeże pobranie z EKW (cache pobrań wycofany).
 * Kosztuje jedno sprawdzenie z limitu planu i trwa ~40 s, więc wołaj wyłącznie
 * z jawnej akcji użytkownika.
 */
export async function ksiegaWieczystaLookup(body: {
  kodWydzialu: string;
  nrKw: string;
  cyfraK: string;
}): Promise<ApiResponse<KwData>> {
  return fetchRailway("/v1/ksiega-wieczysta/lookup", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Historia sprawdzeń KW — same numery i opis nieruchomości, bez treści księgi.
 * Żeby zobaczyć treść, trzeba pobrać ją ponownie (`ksiegaWieczystaLookup`).
 */
export async function ksiegaWieczystaLookups(): Promise<ApiResponse<{ items: KwLookup[] }>> {
  return fetchRailway("/v1/ksiega-wieczysta/lookups");
}

export async function deleteKsiegaWieczystaLookup(id: string): Promise<ApiResponse<{ ok: true }>> {
  return fetchRailway(`/v1/ksiega-wieczysta/lookups/${id}`, { method: "DELETE" });
}

// Onboarding /start — zapis ankiety profilującej. Fire-and-forget (nie blokuje lejka).
export interface OnboardingPatch {
  role?: string;
  roleOther?: string;
  goal?: string;
  volume?: string;
  source?: string;
  toolType?: string;
  completed?: boolean;
}

export async function recordOnboarding(patch: OnboardingPatch): Promise<void> {
  try {
    await fetchRailway("/v1/onboarding", { method: "POST", body: JSON.stringify(patch) });
  } catch {
    /* telemetria nie może blokować onboardingu */
  }
}

export async function assignJobToProject(
  jobId: string,
  projectId: string | null,
): Promise<ApiResponse<{ ok: boolean }>> {
  return fetchRailway("/v1/projects/assign", {
    method: "PATCH",
    body: JSON.stringify({ jobId, projectId }),
  });
}

export async function deleteGalleryItem(jobId: string): Promise<ApiResponse<{ ok: boolean }>> {
  return fetchRailway(`/v1/gallery/items/${encodeURIComponent(jobId)}`, { method: "DELETE" });
}

// Wgraj własne zdjęcie z dysku (nie z narzędzi AI). `sourceImage` to data URL;
// `projectId` opcjonalnie przypina od razu do oferty. Własne zdjęcia są darmowe.
export async function uploadOwnPhoto(
  sourceImage: string,
  projectId: string | null,
): Promise<ApiResponse<{ jobId: string; resultImage: string; thumbnailImage: string | null }>> {
  return fetchRailway("/v1/gallery/upload", {
    method: "POST",
    body: JSON.stringify({ sourceImage, projectId }),
  });
}

// =====================================================
// Admin (Railway) — requires ADMIN_EMAILS env var on API
// =====================================================

export type Period = 1 | 7 | 30 | 90 | "all";

export interface AdminStripeStats {
  activeSubscriptions: number;
  activeSubscriptionsByTier: Record<string, number>;
  mrrPln: number;
  arrPln: number;
  newSubscriptionsInPeriod: number;
  canceledSubscriptionsInPeriod: number;
  totalPaymentsCount: number;
  totalRevenuePln: number;
  packageRevenuePln: number;
  subscriptionRevenuePln: number;
}

export interface AdminStats {
  period: { days: Period };
  totals: {
    users: number;
    newUsersInPeriod: number;
    paidUsers: number;
    agencies: number;
    paidAgencies: number;
    totalJobsDone: number;
    totalJobsFailed: number;
    creditsBoughtRange: number;
    creditsUsedRange: number;
  };
  finance: {
    revenuePln: number;
    transactionCount: number;
    avgTransactionPln: number;
    arpuPln: number;
    packages: Array<{ id: string; name: string; credits: number; pricePln: number; soldCount: number; revenuePln: number }>;
  };
  stripe: AdminStripeStats;
  dailyStats: Array<{ day: string; signups: number; newAgencies: number; jobsDone: number; revenuePln: number; creditsBought: number; creditsUsed: number }>;
  toolUsage: Record<string, { done: number; failed: number }>;
  feedback: {
    total: number;
    q1Counts: Record<string, number>;
    q2Counts: Record<string, number>;
    withFreeText: number;
    list: Array<{
      id: string;
      user_id: string | null;
      anonymous_id: string | null;
      email: string | null;
      resolved_email: string | null;
      q1: string | null;
      q2: string | null;
      q3: string | null;
      created_at: string;
    }>;
  };
  generatedAt: string;
}

export type AdminFeedbackEntry = AdminStats["feedback"]["list"][number];

export interface AdminAgency {
  id: string;
  name: string;
  ownerEmail: string | null;
  membersCount: number;
  balance: number;
  isPaid: boolean;
  createdAt: string;
}

export interface AdminUser {
  user: { id: string; email?: string; createdAt: string };
  balance: { balance: number; corrections: number; isPaid: boolean; subscriptionTier?: string } | null;
  agency: { id: string; name: string; role: string; balance?: { subscriptionTier?: string } | null } | null;
  recentJobs: Array<{ id: string; status: string; variantType: string; createdAt: string }>;
  // Historia kredytow — refreshe/poprawki widoczne jako type 'correction' /
  // 'admin_grant_correction' (processing_jobs nie ma znacznika refreshu).
  transactions?: Array<{ id: string; amount: number; type: string; description: string | null; createdAt: string }>;
}

export async function getAdminStats(days: Period = 30): Promise<ApiResponse<AdminStats>> {
  return fetchRailway(`/v1/admin/stats?days=${days}`);
}

export async function getAdminAgencies(): Promise<ApiResponse<{ count: number; agencies: AdminAgency[] }>> {
  return fetchRailway("/v1/admin/agencies");
}

export interface AdminAgencyMember {
  userId: string;
  email: string | null;
  role: string;
  firstName: string | null;
  lastName: string | null;
  joinedAt: string;
  jobsDone: number;
}

export interface AdminAgencyDetails {
  id: string;
  name: string;
  ownerUserId: string;
  ownerEmail: string | null;
  logoUrl: string | null;
  address: string | null;
  nip: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  createdAt: string;
  balance: number;
  correctionsBalance: number;
  isPaid: boolean;
  subscriptionTier: string;
  members: AdminAgencyMember[];
  pendingInvitations: Array<{ email: string; createdAt: string; expiresAt: string }>;
}

export async function getAdminAgencyDetails(
  agencyId: string,
): Promise<ApiResponse<{ agency: AdminAgencyDetails }>> {
  return fetchRailway(`/v1/admin/agencies/${encodeURIComponent(agencyId)}`);
}

export async function adminFindUser(email: string): Promise<ApiResponse<AdminUser>> {
  return fetchRailway(`/v1/admin/users/search?email=${encodeURIComponent(email)}`);
}

export async function adminGrantCredits(
  userId: string,
  amount: number,
  reason: string,
  grantType: "credits" | "corrections",
): Promise<ApiResponse<{ newBalance: number; transactionId: string }>> {
  return fetchRailway(`/v1/admin/users/${encodeURIComponent(userId)}/grant`, {
    method: "POST",
    body: JSON.stringify({ amount, reason, grantType }),
  });
}

// ─── Brand settings ───────────────────────────────────────────────────────────

export interface BrandSettings {
  id: string;
  userId: string | null;
  agencyId: string | null;
  logoUrl: string | null;
  watermarkOn: boolean;
  watermarkPos: string;
  watermarkOpacity: number;
  watermarkScale: number;
  paletteId: string;
  paletteColors: string[];
  agentName: string;
  agentTitle: string;
  agentPhone: string;
  agentEmail: string;
  agentAgency: string;
  agentLicense: string;
  agentPhotoUrl: string | null;
  footerVariant: number;
}

export type SaveBrandInput = Partial<Omit<BrandSettings, "id" | "userId" | "agencyId">>;

export async function getBrand(): Promise<ApiResponse<{ settings: BrandSettings | null }>> {
  return fetchRailway("/v1/brand");
}

export async function saveBrand(input: SaveBrandInput): Promise<ApiResponse<{ settings: BrandSettings }>> {
  return fetchRailway("/v1/brand", {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function adminSetPlan(
  userId: string,
  tier: "free" | "agent" | "biuro" | "agencja",
): Promise<ApiResponse<{ ok: boolean; tier: string }>> {
  return fetchRailway(`/v1/admin/users/${encodeURIComponent(userId)}/plan`, {
    method: "PUT",
    body: JSON.stringify({ tier }),
  });
}

export interface UserListRow {
  id: string;
  email: string;
  createdAt: string;
  balance: number;
  isPaid: boolean;
  subscriptionTier: string;
  phone: string | null;
  lastSignInAt: string | null;
}

export type AdminUserSortKey = "email" | "subscriptionTier" | "balance" | "createdAt" | "lastSignInAt";

export async function getAdminUsers(
  page = 1,
  limit = 50,
  /** Fragment e-maila lub telefonu — filtruje po całej bazie, nie po bieżącej stronie. */
  search?: string,
  /** Sortowanie robi serwer po CAŁEJ bazie (przed stronicowaniem). */
  sort: AdminUserSortKey = "createdAt",
  dir: "asc" | "desc" = "desc",
): Promise<ApiResponse<{ users: UserListRow[]; total: number }>> {
  const q = search?.trim() ? `&search=${encodeURIComponent(search.trim())}` : "";
  return fetchRailway(`/v1/admin/users?page=${page}&limit=${limit}${q}&sort=${sort}&dir=${dir}`);
}

export interface ExportUserRow {
  email: string | null;
  phone: string | null;
  userId: string;
  createdAt: string;
  lastSignInAt: string | null;
  balance: number;
  correctionsBalance: number;
  isPaid: boolean;
  balanceUpdatedAt: string | null;
}

/** Pełny dump userów (JSON) — CSV składany po stronie klienta. */
export async function exportAdminUsers(): Promise<ApiResponse<{ count: number; users: ExportUserRow[] }>> {
  return fetchRailway("/v1/admin/users/export");
}

export async function adminDeleteUser(userId: string): Promise<ApiResponse<{ ok: boolean; email: string | null }>> {
  return fetchRailway(`/v1/admin/users/${encodeURIComponent(userId)}`, { method: "DELETE" });
}

// ─── Admin onboarding (ankieta /start) ────────────────────────────────────────

export interface OnboardingRow {
  user_id: string;
  /** E-mail z auth — null gdy konto usunięte lub nie udało się rozwiązać. */
  email: string | null;
  role: string | null;
  role_other: string | null;
  goal: string | null;
  volume: string | null;
  source: string | null;
  tool_type: string | null;
  started_at: string | null;
  completed_at: string | null;
  updated_at: string | null;
}

export interface OnboardingResponse {
  count: number;
  completed: number;
  byRole: Record<string, number>;
  byGoal: Record<string, number>;
  byVolume: Record<string, number>;
  bySource: Record<string, number>;
  byTool: Record<string, number>;
  rows: OnboardingRow[];
}

export interface AdminWaitlistRow {
  feature: string;
  channels: string[];
  note: string | null;
  projectId: string | null;
  createdAt: string;
  updatedAt: string;
  /** null, gdy nie udało się rozwiązać maila z GoTrue — wiersz nadal niesie sygnał. */
  email: string | null;
}

export interface AdminWaitlistResponse {
  count: number;
  byFeature: Record<string, number>;
  optionsByFeature: Record<string, Record<string, number>>;
  rows: AdminWaitlistRow[];
}

/** Zgłoszenia na funkcje w przygotowaniu — WSZYSTKICH userów (admin). */
export async function getAdminWaitlist(): Promise<ApiResponse<AdminWaitlistResponse>> {
  return fetchRailway("/v1/admin/waitlist");
}

export async function getAdminOnboarding(): Promise<ApiResponse<OnboardingResponse>> {
  return fetchRailway("/v1/admin/onboarding");
}

// ─── Admin checkouts ──────────────────────────────────────────────────────────

export interface AbandonedCheckout {
  id: string;
  stripe_session_id: string;
  user_id: string | null;
  anonymous_id: string | null;
  email: string | null;
  package_id?: string | null;
  package_name: string | null;
  credits: number;
  amount_pln: number;
  status: "open" | "completed" | "expired";
  expires_at: string | null;
  recovery_email_sent_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface EnrichedAdminCheckout extends AbandonedCheckout {
  kind: "package" | "subscription";
  stripe_customer_id: string | null;
  subscription_tier: string | null;
  subscription_status: string | null;
  stripe_dashboard_url: string;
}

export interface CheckoutsResponse {
  period: { days: number | "all"; since: string | null };
  summary: {
    total: number; open: number; expired: number; completed: number;
    conversionRate: number; abandonedRevenuePln: number; completedRevenuePln: number;
  };
  checkouts: EnrichedAdminCheckout[];
}

export async function getAdminCheckouts(days: number | "all" = 30): Promise<ApiResponse<CheckoutsResponse>> {
  return fetchRailway(`/v1/admin/checkouts?days=${days}`);
}

// ─── Admin generations ────────────────────────────────────────────────────────

export interface AdminGeneration {
  id: string;
  userId: string | null;
  /** E-mail autora rozwiązany z auth — null gdy konto usunięte lub GoTrue zawiódł. */
  userEmail: string | null;
  variantType: string;
  status: string;
  inputImage: string | null;
  thumbnailImage: string | null;
  resultImage: string | null;
  createdAt: string;
}

export async function getAdminGenerations(
  page = 1,
  limit = 24,
): Promise<ApiResponse<{ generations: AdminGeneration[]; total: number }>> {
  return fetchRailway(`/v1/admin/generations?page=${page}&limit=${limit}`);
}

// ─── Admin: podgląd ofert użytkownika ─────────────────────────────────────────
// Wyłącznie odczyt — admin ogląda oferty klienta, nie przejmuje jego sesji.

export interface AdminUserProject {
  id: string;
  name: string;
  address: string | null;
  status: string;
  propertyType: string | null;
  city: string | null;
  street: string | null;
  areaM2: number | null;
  rooms: number | null;
  floor: number | null;
  price: number | null;
  condition: string | null;
  kwNumber: string | null;
  wizardStep: number;
  createdAt: string;
  photoCount: number;
}

export interface AdminProjectPhoto {
  id: string;
  variantType: string;
  status: string;
  thumbnailImage: string | null;
  resultImage: string | null;
  createdAt: string;
}

export interface AdminProjectDetails extends AdminUserProject {
  userId: string;
  description: string | null;
  photos: AdminProjectPhoto[];
}

/** Trafienie wyszukiwarki ofert — to samo co na liście, plus właściciel. */
export interface AdminProjectSearchHit extends AdminUserProject {
  userId: string;
  userEmail: string | null;
}

export interface AdminProjectsListResponse {
  projects: AdminProjectSearchHit[];
  total: number;
  page: number;
  limit: number;
  count?: number;
}

export interface ListAdminProjectsOptions {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

/** Pełna lista wszystkich ofert klientów w panelu admina z paginacją i filtrowaniem. */
export async function listAdminProjects(
  options: ListAdminProjectsOptions = {},
): Promise<ApiResponse<AdminProjectsListResponse>> {
  const query = new URLSearchParams();
  if (options.page != null) query.set("page", String(options.page));
  if (options.limit != null) query.set("limit", String(options.limit));
  if (options.search?.trim()) query.set("search", options.search.trim());
  if (options.status?.trim()) query.set("status", options.status.trim());
  const qs = query.toString();
  return fetchRailway(`/v1/admin/projects${qs ? `?${qs}` : ""}`);
}

/** Szukanie ofert po całej bazie: nazwa, adres, miasto, ulica, numer KW lub ID oferty. */
export async function searchAdminProjects(
  search: string,
): Promise<ApiResponse<AdminProjectsListResponse>> {
  return listAdminProjects({ search });
}

export async function getAdminUserProjects(
  userId: string,
): Promise<ApiResponse<{ userId: string; userEmail: string | null; count: number; projects: AdminUserProject[] }>> {
  return fetchRailway(`/v1/admin/users/${encodeURIComponent(userId)}/projects`);
}

export async function getAdminProject(
  projectId: string,
): Promise<ApiResponse<{ project: AdminProjectDetails }>> {
  return fetchRailway(`/v1/admin/projects/${encodeURIComponent(projectId)}`);
}

// ─── Admin blog ───────────────────────────────────────────────────────────────
// Uwaga: blog_posts ma RLS — anon widzi tylko is_published=true i nie może pisać.
// Cały CRUD idzie przez Railway (service_role), nigdy przez klienta Supabase.

export interface AdminBlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  thumbnail_url: string | null;
  content_html: string;
  category: string;
  read_time: string;
  is_published: boolean;
  published_at: string | null;
  author: string;
  seo_keywords: string | null;
  created_at: string;
  updated_at: string;
}

/** Pola zapisywane przez panel. `excerpt`/`content_html` są NOT NULL → pusty string. */
export interface AdminBlogPostInput {
  slug: string;
  title: string;
  excerpt: string;
  content_html: string;
  is_published: boolean;
  thumbnail_url?: string | null;
  category?: string;
  read_time?: string;
  author?: string;
  seo_keywords?: string | null;
  /** ISO z offsetem; null/brak = automatyczne stemplowanie przy pierwszej publikacji. */
  published_at?: string | null;
}

type BlogSaveResponse = { post: { id: string; slug: string; is_published: boolean }; action: "created" | "updated" };

export async function getAdminBlogPosts(): Promise<ApiResponse<{ posts: AdminBlogPost[] }>> {
  return fetchRailway("/v1/admin/blog");
}

export async function createAdminBlogPost(input: AdminBlogPostInput): Promise<ApiResponse<BlogSaveResponse>> {
  return fetchRailway("/v1/admin/blog", { method: "POST", body: JSON.stringify(input) });
}

export async function updateAdminBlogPost(
  id: string,
  input: AdminBlogPostInput,
): Promise<ApiResponse<BlogSaveResponse>> {
  return fetchRailway(`/v1/admin/blog/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deleteAdminBlogPost(
  id: string,
): Promise<ApiResponse<{ ok: boolean; deleted: { id: string; slug: string } }>> {
  return fetchRailway(`/v1/admin/blog/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export interface AdminBlogAutomationHistoryItem {
  slotKey: string;
  slot: "morning" | "noon";
  id: string;
  slug: string;
  title: string;
  category: string;
  thumbnailUrl: string | null;
  publishedAt: string;
}

export interface AdminBlogAutomationState {
  enabled: boolean;
  schedule: string[];
  timezone: string;
  completedSlots: string[];
  lastRunAt: string | null;
  lastSlotKey: string | null;
  lastError: string | null;
  history: AdminBlogAutomationHistoryItem[];
}

export async function getAdminBlogAutomation(): Promise<ApiResponse<AdminBlogAutomationState>> {
  return fetchRailway("/v1/admin/blog/automation");
}

export async function updateAdminBlogAutomation(
  enabled: boolean,
): Promise<ApiResponse<{ ok: boolean; state: AdminBlogAutomationState }>> {
  return fetchRailway("/v1/admin/blog/automation", {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });
}

export async function autoGenerateAdminBlogPost(params: {
  slot: "morning" | "noon";
  customTopicHint?: string;
}): Promise<
  ApiResponse<{
    ok: boolean;
    post: {
      id: string;
      slug: string;
      title: string;
      excerpt: string;
      category: string;
      read_time: string;
      seo_keywords: string;
      thumbnail_url: string | null;
      published_at: string;
      slot: "morning" | "noon";
    };
  }>
> {
  return fetchRailway("/v1/admin/blog/auto-generate", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// ─── Weryfikacja e-maila ──────────────────────────────────────────────────────

/**
 * Ponowna wysyłka linku weryfikacyjnego (Resend, rate limit 3/15 min po stronie API).
 * `alreadyVerified: true` → user_metadata.af_email_verified jest już ustawione.
 */
export async function sendVerificationEmail(): Promise<
  ApiResponse<{ ok: boolean; emailSent?: boolean; alreadyVerified?: boolean }>
> {
  return fetchRailway("/v1/auth/send-verification-email", { method: "POST" });
}

// ─── Referral ─────────────────────────────────────────────────────────────────

/** Zapisuje polecenie po rejestracji. Idempotentne po stronie API — bezpieczne
 *  do wołania przy każdym SIGNED_IN, bo kod w localStorage kasujemy dopiero po
 *  rozstrzygnięciu (sukces LUB odrzucenie), nie po samej próbie. */
export async function recordReferral(
  code: string,
): Promise<ApiResponse<{ success: boolean; alreadyRecorded?: boolean }>> {
  return fetchRailway("/v1/referral/record", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

// ─── Feedback ─────────────────────────────────────────────────────────────────

export interface FeedbackConfig {
  trigger1Jobs: number;
  trigger2Jobs: number;
  rewardCredits: number;
  enabled: boolean;
}

export async function getFeedbackConfig(): Promise<ApiResponse<FeedbackConfig>> {
  return fetchRailway("/v1/feedback/config");
}

export async function checkFeedbackGiven(): Promise<ApiResponse<{ given: boolean }>> {
  return fetchRailway("/v1/feedback/check");
}

export async function submitFeedback(data: {
  q1: string; q2: string; q3?: string;
}): Promise<ApiResponse<{ ok: boolean; creditsAwarded: number; alreadyGiven: boolean }>> {
  return fetchRailway("/v1/feedback", { method: "POST", body: JSON.stringify(data) });
}

export async function redeemCode(code: string): Promise<ApiResponse<{ ok: boolean; creditsAdded: number; newBalance: number }>> {
  return fetchRailway("/v1/codes/redeem", { method: "POST", body: JSON.stringify({ code }) });
}

// ─── Analityka behawioralna (Railway, admin) ──────────────────────────────────

export interface CohortCell { weekOffset: number; activeUsers: number; rate: number }
export interface CohortRow { cohortWeek: string; cohortSize: number; cells: CohortCell[] }
export interface CohortRetention { weeks: number; cohorts: CohortRow[] }

export interface ActivationFunnel {
  /** null = zakres „od początku". */
  periodDays: number | null;
  signups: number;
  withFirstJob: number;
  withThreeJobs: number;
  withPurchase: number;
}

export interface ChurnRow {
  userId: string;
  email: string | null;
  subscriptionTier: string;
  balance: number;
  lastJobAt: string | null;
}

export interface Ga4Report {
  report: string;
  dimensionHeaders: string[];
  metricHeaders: string[];
  rows: Array<{ dimensions: string[]; metrics: number[] }>;
  totals?: number[];
}

export interface GscDailyRow {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface GscQueryRow {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface GscReport {
  siteUrl: string;
  summary: {
    totalClicks: number;
    totalImpressions: number;
    avgCtr: number;
    avgPosition: number;
  };
  daily: GscDailyRow[];
  topQueries: GscQueryRow[];
}

export interface ClarityMetrics {
  totalSessions: number;
  distinctUsers: number;
  pagesPerSession: number;
  scrollDepth: number;
  deadClickRate: number;
  rageClickRate: number;
  quickBackRate: number;
  rageClicks: number;
  deadClicks: number;
  quickBacks: number;
}

export interface ClarityPopularPage {
  url: string;
  visits: number;
}

export interface ClarityBreakdownItem {
  name: string;
  sessions: number;
}

export interface ClarityReport {
  projectId: string;
  dashboardUrl: string;
  numOfDays?: number;
  metrics: ClarityMetrics;
  popularPages?: ClarityPopularPage[];
  devices?: ClarityBreakdownItem[];
  browsers?: ClarityBreakdownItem[];
  total_sessions?: number;
  distinct_users?: number;
  avg_scroll_depth?: number;
  rage_clicks?: number;
  dead_clicks?: number;
  quick_backs?: number;
  playback_url_base?: string;
}

export interface AnalyticsOverviewResult {
  period: { days: number; startDate: string; endDate: string };
  ga4: { configured: boolean; report?: Ga4Report; error?: string };
  gsc: { configured: boolean; report?: GscReport; error?: string };
  clarity: { configured: boolean; report?: ClarityReport; error?: string };
}

export async function getAnalyticsCohorts(weeks = 8): Promise<ApiResponse<CohortRetention>> {
  return fetchRailway(`/v1/admin/analytics/cohorts?weeks=${weeks}`);
}

/** Zakres analityki: liczba dni albo "all" (od początku). */
export type AnalyticsRange = number | "all";

export async function getAnalyticsActivation(days: AnalyticsRange = 30): Promise<ApiResponse<ActivationFunnel>> {
  return fetchRailway(`/v1/admin/analytics/activation?days=${days}`);
}

export async function getAnalyticsChurn(
  days: AnalyticsRange = 30,
): Promise<ApiResponse<{ days: number | null; rows: ChurnRow[] }>> {
  return fetchRailway(`/v1/admin/analytics/churn?days=${days}`);
}

export async function getGa4Report(report = "traffic", days = 28): Promise<ApiResponse<Ga4Report>> {
  return fetchRailway(`/v1/admin/analytics/ga4?report=${encodeURIComponent(report)}&days=${days}`);
}

export async function getAnalyticsOverview(
  days: AnalyticsRange = 28,
): Promise<ApiResponse<AnalyticsOverviewResult>> {
  const daysParam = days === "all" ? 365 : days;
  return fetchRailway(`/v1/admin/analytics/overview?days=${daysParam}`);
}

export async function getGscReport(
  days = 28,
): Promise<ApiResponse<{ configured: boolean; report?: GscReport; error?: string }>> {
  return fetchRailway(`/v1/admin/analytics/gsc?days=${days}`);
}

export async function getClarityReport(
  days = 28,
): Promise<ApiResponse<{ configured: boolean; report?: ClarityReport; error?: string }>> {
  return fetchRailway(`/v1/admin/analytics/clarity?days=${days}`);
}

// ─── Doradca AI / Copilot (Railway, admin) ────────────────────────────────────

export interface CopilotMessage { role: "user" | "assistant"; content: string }
export interface CopilotChatResult { reply: string; toolCalls: number; rounds: number; modelUsed: string }

export interface OkrKeyResult {
  id: string;
  measure: string;
  measureType: "lead" | "lag";
  target: number | null;
  current: number | null;
  unit: string | null;
}
export interface Okr {
  id: string;
  wig: string;
  fromValue: number | null;
  toValue: number | null;
  unit: string | null;
  dueDate: string | null;
  status: "active" | "done" | "dropped";
  keyResults: OkrKeyResult[];
}

export interface DailyReport {
  id: string;
  reportDate: string;
  contentMd: string;
  metrics: Record<string, unknown> | null;
  modelUsed: string | null;
  createdAt: string;
}

export async function copilotChat(messages: CopilotMessage[]): Promise<ApiResponse<CopilotChatResult>> {
  return fetchRailway("/v1/admin/copilot/chat", { method: "POST", body: JSON.stringify({ messages }) });
}

export async function getCopilotOkrs(): Promise<ApiResponse<{ okrs: Okr[] }>> {
  return fetchRailway("/v1/admin/copilot/okr");
}

export async function saveCopilotOkr(input: {
  id?: string;
  wig: string;
  fromValue?: number | null;
  toValue?: number | null;
  unit?: string | null;
  dueDate?: string | null;
  status?: "active" | "done" | "dropped";
}): Promise<ApiResponse<{ ok: boolean; id: string }>> {
  return fetchRailway("/v1/admin/copilot/okr", { method: "POST", body: JSON.stringify(input) });
}

export async function saveCopilotKeyResult(input: {
  id?: string;
  okrId: string;
  measure: string;
  measureType?: "lead" | "lag";
  target?: number | null;
  current?: number | null;
  unit?: string | null;
}): Promise<ApiResponse<{ ok: boolean }>> {
  return fetchRailway("/v1/admin/copilot/okr/key-result", { method: "POST", body: JSON.stringify(input) });
}

export async function getCopilotDailyReport(): Promise<ApiResponse<{ report: DailyReport | null }>> {
  return fetchRailway("/v1/admin/copilot/daily-report/latest");
}

// ─── Lista oczekujących na funkcje w przygotowaniu (painted-door) ─────────────

export interface WaitlistSignup {
  feature: string;
  channels: string[];
  note: string | null;
  createdAt: string;
}

export async function getWaitlistSignups(): Promise<ApiResponse<{ signups: WaitlistSignup[] }>> {
  return fetchRailway("/v1/waitlist");
}

export async function joinWaitlist(input: {
  feature: string;
  channels?: string[];
  note?: string;
  projectId?: string;
}): Promise<ApiResponse<{ ok: boolean }>> {
  return fetchRailway("/v1/waitlist", { method: "POST", body: JSON.stringify(input) });
}
